import React, { useState, useEffect } from 'react';
import { X, CreditCard, Receipt, Wallet, ChevronRight } from 'lucide-react';
import { createPayment } from '../lib/payments';

interface RecordPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bookingId: string;
  businessId: string;
  bookingTotal: number;
  amountPaid: number;
}

const paymentMethods = [
  {
    id: 'credit_card',
    name: 'Record Credit / Debit',
    icon: <CreditCard className="h-5 w-5 text-blue-500" />,
    description: 'Record credit or debit card payment'
  },
  {
    id: 'bank_transfer',
    name: 'Record Bank Transfer',
    icon: <Receipt className="h-5 w-5 text-green-500" />,
    description: 'Record direct bank transfer payment'
  },
  {
    id: 'cash',
    name: 'Record Cash',
    icon: <Wallet className="h-5 w-5 text-yellow-500" />,
    description: 'Record cash payment'
  },
  {
    id: 'stripe',
    name: 'Take Stripe Payment',
    icon: <CreditCard className="h-5 w-5 text-purple-500" />,
    description: 'Process payment through Stripe'
  }
];

export default function RecordPaymentModal({
  isOpen,
  onClose,
  onSuccess,
  bookingId,
  businessId,
  bookingTotal,
  amountPaid
}: RecordPaymentModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState(paymentMethods[0]);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Set default amount to remaining balance with proper decimal handling
      const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
      setAmount(remainingBalance.toFixed(2));
    }
  }, [isOpen, bookingTotal, amountPaid]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setSelectedMethod(paymentMethods[0]);
      setAmount('');
      setReference('');
      setNotes('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      // Validate amount
      const paymentAmount = parseFloat(amount);
      if (isNaN(paymentAmount) || paymentAmount <= 0) {
        throw new Error('Please enter a valid amount');
      }

      // Validate remaining balance with proper decimal handling for currency
      // Round all values to 2 decimal places to avoid floating point precision issues
      const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
      const roundedPaymentAmount = Math.round(paymentAmount * 100) / 100;
      
      if (roundedPaymentAmount > remainingBalance) {
        throw new Error(`Payment amount cannot exceed remaining balance of $${remainingBalance.toFixed(2)}`);
      }

      await createPayment({
        business_id: businessId,
        booking_id: bookingId,
        amount: paymentAmount,
        method: selectedMethod.id as any,
        status: 'completed',
        reference: reference || null,
        notes: notes || null
      });

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error recording payment:', err);
      setError(err instanceof Error ? err.message : 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-50`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Record Payment</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Payment Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-gray-500">Total Amount</div>
                    <div className="mt-1 text-sm font-medium text-gray-900">
                      ${(Math.round(bookingTotal * 100) / 100).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Amount Paid</div>
                    <div className="mt-1 text-sm font-medium text-green-600">
                      ${(Math.round(amountPaid * 100) / 100).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Balance Due</div>
                    <div className="mt-1 text-sm font-medium text-red-600">
                      ${(Math.round((bookingTotal - amountPaid) * 100) / 100).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Payment Method
                </label>
                <div className="space-y-2">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setSelectedMethod(method)}
                      className={`w-full flex items-center p-4 border rounded-lg ${
                        selectedMethod.id === method.id
                          ? 'border-gray-900 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center mr-4">
                        {method.icon}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-sm font-medium text-gray-900">{method.name}</div>
                        <div className="text-sm text-gray-500">{method.description}</div>
                      </div>
                      {selectedMethod.id === method.id && (
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Details */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-6">Payment Details</h3>
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Amount
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="w-full pl-7 pr-4 h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      className="w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                      placeholder="Enter reference number"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      rows={4}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm p-3"
                      placeholder="Add any additional notes about this payment..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading || !amount || parseFloat(amount) <= 0}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Record Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}